import 'nkba-bootstrap/dist/css/bootstrap.css';
import 'font-awesome/css/font-awesome.css';
import './App.css';

import * as React from 'react';

import { Entities, EntityActions, EntityType } from './common/entity';
import { withRouter, Route, Switch, RouteComponentProps } from 'react-router-dom';
import { axios, getToken, setToken } from './common/api/axios.config';
import { isSuperuser, isChaptersAdmin } from './common/utils/acl';

import { AgingReport } from './screens/Reports/AgingReport/AgingReport';
import { AppState } from 'common/models/AppState';
import { Chapters } from './screens/Chapters/Chapters';
import { Checkout } from './screens/Checkout/Checkout';
import { Upload } from './screens/Upload/Upload';
import { Dashboard } from './screens/Dashboard/Dashboard';
import { Exports } from './screens/Exports/Exports';
import { Navigator } from './common/components/layout/Navigator';
import { OauthApi } from './common/api/resources/OauthApi';
import { Orders } from './screens/Orders/Orders';
import { Orgs } from './screens/Orgs/Orgs';
import { Products } from './screens/Products/Products';
import { ReleaseNotes } from './screens/ReleaseNotes/ReleaseNotes';
import { Tags } from './screens/Tags/Tags';
import { VftiRegistrants } from './screens/VftiRegistrants/VftiRegistrants';
import { SummitRegistrants } from './screens/SummitRegistrants/SummitRegistrants';
import { Transactions } from './screens/Transactions/Transactions';
import { MemberPermissions } from 'common/api/models/MemberPermissions';
import { UserSettings } from './screens/UserSettings/UserSettings';
import { Users } from './screens/Users/Users';
import { toApiError } from './common/utils/api';
import { MemberApi } from './common/api/resources/MemberApi';
import { ConfigScreen } from './screens/Config/ConfigScreen';
import { ConfigApi } from './common/api/resources/ConfigApi';

class AppImpl extends React.Component<RouteComponentProps<{}>, AppState> {
  state: AppState = { entities: new Entities(), inProgress: 0, permissions: [] };
  inProgress: Map<string, Promise<any>> = new Map();

  entityActions: EntityActions = {
    get: (type: EntityType, id: string) => {
      const key = String(type);
      return this.state.entities[key][id];
    },

    fetch: (type: EntityType, id: string) => {
      const key = String(type);
      if (id && this.state.entities[key][id]) {
        return Promise.resolve(this.state.entities[key][id]);
      }
      return this.entityActions.refetch(type, id);
    },

    fetchIfPresent: (type: EntityType, id: string | undefined) => {
      if (!id) {
        return;
      }
      this.entityActions.fetch(type, id);
    },

    put: (type: EntityType, id: string, entity: any) => {
      const key = String(type);
      this.setState(state => {
        if (type === EntityType.CONFIG) {
          return {
            ...state,
            config: entity,
          };
        }

        return {
          ...state,
          entities: {
            ...state.entities,
            [key]: {
              ...state.entities[key],
              [id]: entity,
            },
          },
          ...(type === EntityType.MEMBER && this.state.user && id === this.state.user.id ? { user: entity } : {}),
        };
      });
    },

    putAll: (type: EntityType, data: any[], clearExisting?: boolean) => {
      let map: { [id: string]: any } = {};
      for (let d of data) {
        map[(d as any).id] = d;
      }
      this.entityActions.putMap(type, map, clearExisting);
    },

    /**
     * Puts a map of values into the store, notifying app with setState
     */
    putMap: (type: EntityType, map: { [id: string]: any }, clearExisting?: boolean) => {
      this.setState((state: AppState) => {
        const newEntities = clearExisting ? map : { ...state.entities[String(type)], ...map };
        return { ...state, entities: { ...state.entities, [String(type)]: newEntities } };
      });
    },

    /* Force a reload of an entity */
    refetch: (type: EntityType, id: string = '') => {
      const key = String(type);
      if (!this.inProgress[key + id]) {
        this.setState(prevState => ({ ...prevState, inProgress: prevState.inProgress + 1 }));
        const url = type === EntityType.MEMBER ? '/v2/member/' + id + '?expandTags=true' : '/v1/' + key + '/' + id;
        this.inProgress[key + id] = axios.get(url).then(
          res => {
            this.entityActions.put(type, id, res.data);
            this.setState(prevState => ({ ...prevState, inProgress: prevState.inProgress - 1 }));
            this.inProgress[key + id] = undefined;
            return res.data;
          },
          err => {
            this.setState(prevState => ({
              ...prevState,
              fetchError: toApiError(err),
              inProgress: prevState.inProgress - 1,
            }));
            this.inProgress[key + id] = undefined;
            throw err;
          }
        );
      }
      return this.inProgress[key + id];
    },

    refetchIfPresent: (type: EntityType, id: string | undefined) => {
      if (id) {
        this.entityActions.refetch(type, id);
      }
    },

    fetchMissingIds: (type: EntityType, ids: string[]) => {
      const key = String(type);
      const missingIds = ids.filter(id => !this.state.entities[key][id]);
      this.entityActions.fetchIds(type, missingIds);
    },

    fetchIds: (type: EntityType, ids: string[]) => {
      if (!ids || ids.length === 0) {
        return;
      }
      const key = String(type);
      const url =
        type === EntityType.MEMBER
          ? '/v2/member?ids=' + ids.join(',') + '&expandTags=true'
          : '/v1/' + key + '?ids=' + ids.join(',');
      this.setState(prevState => ({ inProgress: prevState.inProgress + 1 }));
      axios.get(url).then(
        res => {
          this.setState(prevState => {
            let entities = { ...prevState.entities, [key]: { ...prevState.entities[key] } };
            for (let obj of res.data.data) {
              entities[key][obj.id] = obj;
            }
            return { entities, inProgress: prevState.inProgress - 1 };
          });
        },
        err => {
          this.setState(prevState => ({
            fetchError: toApiError(err),
            inProgress: prevState.inProgress - 1,
          }));
        }
      );
    },

    fetchAll: (type: EntityType, callback?: () => void) => {
      const key = String(type);
      if (Object.keys(this.state.entities[key]).length === 0) {
        this.entityActions.refetchAll(type, callback);
      } else if (callback) {
        callback();
      }
    },

    refetchAll: (type: EntityType, callback?: () => void) => {
      const key = String(type);
      const url = type === EntityType.MEMBER ? '/v2/member?limit=1000' : '/v1/' + key + '?limit=1000';
      axios.get(url).then(
        res => {
          this.entityActions.putAll(type, res.data.data, true);
          this.setState(
            prevState => ({ ...prevState, inProgress: prevState.inProgress - 1 }),
            () => {
              if (callback) {
                callback();
              }
            }
          );
        },
        err => {
          this.setState(
            prevState => ({ ...prevState, inProgress: prevState.inProgress - 1, fetchError: toApiError(err) }),
            () => {
              if (callback) {
                callback();
              }
            }
          );
        }
      );
    },
  };

  componentDidMount() {
    // The token is set isomorphically through a secure domain cookie in NKBA-web on the domain .nkba.org.
    // In prod, the admin application will have access to this cookie. No need for localStorage.
    const token = getToken();
    if (!!token) {
      this.loadCurrentUser();
      this.loadConfig();
    } else {
      // If in dev, automagically login so that you do not need to run nkba-web in order to develop admin,
      if (
        process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_AUTOLOGIN_USER &&
        process.env.REACT_APP_AUTOLOGIN_PASS
      ) {
        OauthApi.issueAccessToken(
          'password',
          undefined,
          undefined,
          undefined,
          process.env.REACT_APP_AUTOLOGIN_USER,
          process.env.REACT_APP_AUTOLOGIN_PASS
        ).then(
          res => {
            setToken((res as any).access_token as string);
            this.loadCurrentUser();
          },
          err => {
            this.setState({ error: toApiError(err) });
          }
        );
      } else {
        // Boot the user to login
        window.location.replace(
          process.env.REACT_APP_WEB_URL + '/login?redirect=' + encodeURIComponent(window.location.href)
        );
      }
    }
  }

  // Invoked immediately after the component's updates are flushed to the DOM.
  // Not called for the initial render
  componentDidUpdate(prevProps: RouteComponentProps<any>) {
    if (prevProps.location !== this.props.location) {
      (window as any).analytics.page();
    }
  }

  loadCurrentUser = () => {
    // get the user from the token
    MemberApi.getMePermissions().then(
      perm => {
        if (perm.permissions!.indexOf(MemberPermissions.Permissions.ADMIN) === -1) {
          // user is not an admin, so bounce them to the web app
          window.location.replace(process.env.REACT_APP_WEB_URL!);
        } else {
          (window as any).analytics.identify(perm.member!.id!, {
            id: perm.member!.id!,
            email: perm.member!.email!,
            avatar: process.env.REACT_APP_MEDIA_URL + '/' + perm.member!.imagePath!,
            firstName: perm.member!.firstName!,
            lastName: perm.member!.lastName!,
            adminAccess: perm.member!.adminAccess!,
          });
          this.setState({ user: perm.member, permissions: perm.permissions! });
          // load all tags
          this.entityActions.fetchAll(EntityType.TAG);
        }
      },
      err => {
        if (!err.response || !err.response.status || err.response.status !== 401) {
          this.setState({ error: toApiError(err) });
        }
      }
    );
  };

  loadConfig = () => {
    ConfigApi.getConfig().then(
      config => {
        this.setState({ config });
      },
      err => {
        if (!err.response || !err.response.status || err.response.status !== 401) {
          this.setState({ error: toApiError(err) });
        }
      }
    );
  };

  render() {
    if (this.state.error) {
      return (
        <div style={{ marginTop: 50, marginLeft: 50, marginRight: 50 }} className="alert alert-danger">
          {this.state.error}
        </div>
      );
    }
    if (!this.state.user || !this.state.config) {
      return null;
    }
    return (
      <div className="app">
        <Navigator appState={this.state}>
          <Switch>
            <Route path="/" exact={true} render={props => <Dashboard appState={this.state} />} />
            <Route
              path="/users"
              render={props => <Users appState={this.state} {...props} entityActions={this.entityActions} />}
            />
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/tags"
                render={props => <Tags appState={this.state} {...props} entityActions={this.entityActions} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/vftiregistrants"
                render={props => (
                  <VftiRegistrants appState={this.state} {...props} entityActions={this.entityActions} />
                )}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/summitregistrants"
                render={props => (
                  <SummitRegistrants appState={this.state} {...props} entityActions={this.entityActions} />
                )}
              />
            )}
            {isSuperuser(this.state.user) && (
              <Route
                path="/config"
                render={props => <ConfigScreen appState={this.state} entityActions={this.entityActions} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/uploadeligibility"
                render={props => (
                  <Upload
                    appState={this.state}
                    history={props.history}
                    location={props.location}
                    entityActions={this.entityActions}
                  />
                )}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/orgs"
                render={props => <Orgs appState={this.state} {...props} entityActions={this.entityActions} />}
              />
            )}
            <Route
              path="/chapters"
              render={props => <Chapters appState={this.state} {...props} entityActions={this.entityActions} />}
            />
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/settings"
                render={props => <UserSettings appState={this.state} entityActions={this.entityActions} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/products"
                render={props => <Products appState={this.state} entityActions={this.entityActions} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/orders"
                render={props => <Orders appState={this.state} entityActions={this.entityActions} {...props} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/transactions"
                render={props => <Transactions appState={this.state} entityActions={this.entityActions} {...props} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/exports"
                render={props => <Exports appState={this.state} entityActions={this.entityActions} {...props} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/reports/aging"
                render={props => <AgingReport appState={this.state} entityActions={this.entityActions} {...props} />}
              />
            )}
            {!isChaptersAdmin(this.state.user) && (
              <Route
                path="/checkout"
                render={props => (
                  <Checkout
                    appState={this.state}
                    history={props.history}
                    location={props.location}
                    entityActions={this.entityActions}
                  />
                )}
              />
            )}
            {!isChaptersAdmin(this.state.user) && <Route path="/release-notes" component={ReleaseNotes} />}
          </Switch>
        </Navigator>
      </div>
    );
  }
}

export const App = withRouter(AppImpl);



// WEBPACK FOOTER //
// ./src/App.tsx